import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import "./ChildHome.css";
import Emoji from "../components/Emoji";
import ChildBottomNav from "../components/ChildBottomNav";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase/config";

const TASK_CATEGORIES = [
  "Навчання",
  "Вільний час",
  "Рух",
  "Пригоди",
  "Сімейка",
  "Самостійсть",
  "Гігієна"
];
const OTHER_CATEGORY = "Інше";
const CHILD_TASK_COLUMNS = [
  { status: "todo", title: "Доступні завдання" },
  { status: "done", title: "Виконано" }
];

function ChildHome() {
  const [tasks, setTasks] = useState([]);
  const [historyTasks, setHistoryTasks] = useState([]);
  const [taskTemplatesById, setTaskTemplatesById] = useState({});
  const [activeCategories, setActiveCategories] = useState({});
  const [child, setChild] = useState(null);
  const [waitingPoints, setWaitingPoints] = useState(0);
  const today = new Date().toISOString().slice(0, 10);
  const monday = getMonday();
  const hasGenerated = useRef(false);
  const todoColumnRef = useRef(null);
  const childId = child?.id;

  function getMonday() {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().slice(0, 10);
  }

  function getNextMondayString() {
    const date = new Date();
    const day = date.getDay();
    const daysUntilNextMonday = (8 - day) % 7 || 7;
    const nextMonday = new Date(date.setDate(date.getDate() + daysUntilNextMonday));
    return nextMonday.toISOString().slice(0, 10);
  }

  const scrollToTodoColumn = () => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (todoColumnRef.current) {
          todoColumnRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      });
    });
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("selectedUser"));
    if (!user || user.type !== "child") {
      window.location.href = "/";
      return;
    }

    if (!user.id) return;

    setChild(user);

    const unsubscribe = onSnapshot(doc(db, "users", user.id), (snap) => {
      if (!snap.exists()) return;

      const childData = { id: snap.id, ...snap.data() };
      setChild(childData);
      localStorage.setItem("selectedUser", JSON.stringify(childData));
    });

    return unsubscribe;
  }, []);

  const generateTaskHistory = useCallback(async (child) => {
    const taskSnap = await getDocs(collection(db, "tasks"));
    const allTasks = taskSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    for (let task of allTasks) {
      if (task.active === false) continue;
      if (!task.assignedTo.includes(child.id)) continue;

      const dateKey =
        task.repeat === "daily" ? today :
        task.repeat === "weekly" ? monday :
        null;

      const filters = [
        where("child", "==", child.id),
        where("taskId", "==", task.id),
      ];
      if (dateKey) filters.push(where("date", "==", dateKey));

      const existsSnap = await getDocs(query(collection(db, "taskHistory"), ...filters));
      if (!existsSnap.empty) {
        await Promise.all(existsSnap.docs.map(docSnap =>
          updateDoc(doc(db, "taskHistory", docSnap.id), {
            category: task.category || ""
          })
        ));
        continue;
      }

      await addDoc(collection(db, "taskHistory"), {
        taskId: task.id,
        title: task.title,
        description: task.description,
        icon: task.icon,
        points: Number(task.points),
        category: task.category || "",
        repeat: task.repeat,
        child: child.id,
        date: dateKey || today,
        status: "todo",
        completedAt: null,
      });
    }

    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const oldTasks = await getDocs(query(
      collection(db, "taskHistory"),
      where("child", "==", child.id),
      where("repeat", "==", "daily"),
      where("date", "==", yesterday),
      where("status", "==", "todo")
    ));
    oldTasks.forEach(docSnap => deleteDoc(doc(db, "taskHistory", docSnap.id)));
  }, [monday, today]);

  const updateVisibleTasks = useCallback((all, templatesByTaskId, currentChildId) => {
    const tasksWithCategories = all
      .filter(task => {
        const template = templatesByTaskId[task.taskId];
        if (!template) return false;
        if (template.active === false) return false;
        return template.assignedTo?.includes(currentChildId);
      })
      .map(task => ({
        ...task,
        category: templatesByTaskId[task.taskId]?.category ?? task.category ?? ""
      }));

    const filtered = tasksWithCategories.filter((task) => {
      if (task.status === "verified") return false;
      if (task.repeat === "daily") return task.date === today;
      if (task.repeat === "weekly") return task.date === monday;
      return task.repeat === "once";
    });

    const waiting = filtered
      .filter((t) => t.status === "done")
      .reduce((sum, t) => sum + Number(t.points || 0), 0);

    setTasks(filtered);
    setWaitingPoints(waiting);
  }, [monday, today]);

  const loadTasks = useCallback(async (childId) => {
    const q = query(collection(db, "taskHistory"), where("child", "==", childId));
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setHistoryTasks(all);
  }, []);

  useEffect(() => {
    if (child?.id && !hasGenerated.current) {
      hasGenerated.current = true;
      generateTaskHistory(child);
    }
  }, [child, generateTaskHistory]);

  useEffect(() => {
    if (!childId) return undefined;

    const q = query(collection(db, "taskHistory"), where("child", "==", childId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const all = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setHistoryTasks(all);
    });

    return unsubscribe;
  }, [childId]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "tasks"), (snapshot) => {
      const templatesByTaskId = {};
      snapshot.docs.forEach(docSnap => {
        templatesByTaskId[docSnap.id] = docSnap.data();
      });
      setTaskTemplatesById(templatesByTaskId);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    updateVisibleTasks(historyTasks, taskTemplatesById, childId);
  }, [historyTasks, taskTemplatesById, childId, updateVisibleTasks]);

  const markAsDone = async (taskId) => {
    const completedAt = new Date();
    const taskToUpdate = tasks.find((task) => task.id === taskId);

    if (taskToUpdate) {
      setTasks((prev) =>
        prev.map((task) =>
          task.id === taskId ? { ...task, status: "done", completedAt } : task
        )
      );
      setWaitingPoints((prev) => prev + Number(taskToUpdate.points || 0));
    }

    try {
      await updateDoc(doc(db, "taskHistory", taskId), {
        status: "done",
        completedAt,
      });
      await loadTasks(child.id);
    } catch (error) {
      console.error("Не вдалося позначити завдання виконаним:", error);
      await loadTasks(child.id);
    }
  };

  const markAsUndone = async (taskId) => {
    const taskToUpdate = tasks.find((task) => task.id === taskId);

    if (taskToUpdate) {
      setTasks((prev) =>
        prev.map((task) =>
          task.id === taskId ? { ...task, status: "todo", completedAt: null } : task
        )
      );
      setWaitingPoints((prev) => Math.max(0, prev - Number(taskToUpdate.points || 0)));
    }

    try {
      await updateDoc(doc(db, "taskHistory", taskId), {
        status: "todo",
        completedAt: null,
      });
      await loadTasks(child.id);
    } catch (error) {
      console.error("Не вдалося повернути завдання:", error);
      await loadTasks(child.id);
    }
  };

  const grouped = useMemo(() => ({
    todo: tasks.filter((t) => t.status === "todo"),
    done: tasks.filter((t) => t.status === "done"),
  }), [tasks]);

  const groupTasksByCategory = useCallback((statusTasks) => {
    const categoryGroups = TASK_CATEGORIES.map(category => ({
      category,
      tasks: statusTasks.filter(task => task.category === category)
    })).filter(group => group.tasks.length > 0);

    const otherTasks = statusTasks.filter(
      task => !TASK_CATEGORIES.includes(task.category)
    );

    if (otherTasks.length > 0) {
      categoryGroups.push({
        category: OTHER_CATEGORY,
        tasks: otherTasks
      });
    }

    return categoryGroups;
  }, []);

  const todoCategoryGroups = useMemo(
    () => groupTasksByCategory(grouped.todo),
    [groupTasksByCategory, grouped.todo]
  );

  useEffect(() => {
    setActiveCategories(prev => {
      const next = { ...prev };
      let changed = false;

      const hasActiveCategory = todoCategoryGroups.some(group => group.category === next.todo);

      if (!hasActiveCategory) {
        next.todo = todoCategoryGroups[0]?.category || "";
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [todoCategoryGroups]);

  const getFormattedDate = (dateString) => {
    const date = new Date(dateString);
    const dayName = date.toLocaleDateString("uk-UA", { weekday: "long" });
    const day = date.getDate();
    const months = [
      "січня", "лютого", "березня", "квітня", "травня", "червня",
      "липня", "серпня", "вересня", "жовтня", "листопада", "грудня"
    ];
    return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)}, ${day} ${months[date.getMonth()]}`;
  };

  if (!child) return <div>Завантаження...</div>;

  return (
    <div className="child-home">
      <div className="child-app-shell">
      <header className="child-header">
        <div className="child-header-inner">
          <div className="child-profile-row">
            <img src={child.photo} alt={child.name} className="avatar" />
            <h2>{child.name}</h2>
          </div>
          <div className="points-block">
            <div className="points"><Emoji label="Кубок" symbol="🏆" /> {child.points || 0} балів</div>
            <div className="waiting"><Emoji label="Годинник" symbol="🕐" /> В очікуванні: {waitingPoints} балів</div>
          </div>
        </div>
      </header>

      <div className="task-board">
        {CHILD_TASK_COLUMNS.map(({ status, title }) => (
          <div
            ref={status === "todo" ? todoColumnRef : null}
            className={`task-column task-column-${status}`}
            key={status}
          >
            <h3>{title}</h3>
            {status === "todo" ? (
              <div className="task-category-list">
                {todoCategoryGroups.map(group => (
                  <section key={group.category} className="task-category-section">
                    <button
                      type="button"
                      className={`task-category-toggle ${activeCategories.todo === group.category ? "active" : ""}`}
                      onClick={() => {
                        setActiveCategories(prev => ({
                          ...prev,
                          todo: group.category
                        }));
                        scrollToTodoColumn();
                      }}
                    >
                      {group.category}
                    </button>
                    {activeCategories.todo === group.category && (
                      <div className="task-list">
                        {group.tasks.map(task => (
                          <div className="task-card" key={task.id}>
                            <div className="task-title">
                              <span><Emoji label="Іконка завдання" symbol={task.icon || "📝"} /> {task.title}</span>
                              <span className="task-points"><Emoji label="Кубок" symbol="🏆" /> {task.points}</span>
                            </div>
                            <p className="task-date"><strong>Виконати до:</strong> {getFormattedDate(task.repeat === "weekly" ? getNextMondayString() : task.date)}</p>
                            <p className="task-description"><strong>Опис:</strong> {task.description}</p>
                            <button type="button" aria-label="Позначити виконаним" onClick={() => markAsDone(task.id)}>
                              <span aria-hidden="true">✓</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            ) : (
              <div className="task-list">
                {grouped.done.map(task => (
                  <div className="task-card" key={task.id}>
                    <div className="task-title">
                      <span><Emoji label="Іконка завдання" symbol={task.icon || "📝"} /> {task.title}</span>
                      <span className="task-points"><Emoji label="Кубок" symbol="🏆" /> {task.points}</span>
                    </div>
                    <p className="task-date"><strong>Виконати до:</strong> {getFormattedDate(task.repeat === "weekly" ? getNextMondayString() : task.date)}</p>
                    <p className="task-description"><strong>Опис:</strong> {task.description}</p>
                    <button type="button" className="undone-btn" onClick={() => markAsUndone(task.id)}>Повернути</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <ChildBottomNav />
      </div>
    </div>
  );
}

export default ChildHome;
